import { apiProfile } from "@/api/axios-instance";
import { getToken } from "@/auth";
import { apiResponseMiddleware } from "@/middleware/api-response";
import { jwtDecode } from "jwt-decode";

import { ProfileType } from "@/types/profile";
import { ApiResult } from "@/types/api-response";

export interface EditAboutMeType {
  id?: number;
  universityEmail?: string;
  personalEmail?: string;
  phone?: string;
  mobileNumber?: string;
  biographyEn?: string;
  biographyFa?: string;
  lastName?: string;
  firstName?: string;
  orcid?: string;
  googleScholar?: string;
  scopus?: string;
  isi?: string;
  isc?: string;
  nickName: EditNickNameType[];
  moreEmails: EditMoreEmailsType[];
  affilations: EditAffilationsType[];
}
export interface EditNickNameType {
  name: string;
}

export interface EditAffilationsType {
  title: string;
}

export interface EditMoreEmailsType {
  email: string;
}

export interface EditResearchType {
  id?: number;
  areaOfStudy?: string;
  areaOfStudyFa?: string;
  research?: string;
  researchFA?: string;
  membershipEn?: string;
  membershipFa?: string;
  researchAreas?: {
    title?: string;
    titleFa?: string;
  }[];

  professionalActivityEn?: string;
  professionalActivityFa?: string;
  professionalActivities?: {
    title?: string;
    titleFa?: string;
  }[];

  memberships?: {
    title?: string;
    titleFa?: string;
  }[];
}

export interface EditEducationType {
  id?: number;
  teachingInterestEn?: string;
  teachingInterestFa?: string;
  teachingInterests?: {
    title?: string;
    titleFa?: string;
  }[];

  educations?: {
    title?: string;
    titleFa?: string;
    universityFa?: string;
    universityEn?: string;
    degree?: string | null;
    countryFa?: string;
    countryEn?: string;
    cityFa?: string;
    cityEn?: string;
  }[];
}

export interface EditSocialLinksType {
  id?: number;
  webOfScience?: string;
  linkedIn?: string;
  orcid?: string;
  researchGate?: string;
  personalWebsite?: string;
  googleScholar?: string;
  scopus?: string;
  isi?: string;
  facebook?: string;
  gmail?: string;
  instagram?: string;
  twitter?: string;
  mendeley?: string;
  academia?: string;
  eitaa?: string;
  isc?: string;
  webLinks?: {
    title?: string;
    titleFa?: string;
    link?: string;
  }[];
}

export interface EditNewsItemType {
  title: string;
  content: string;
}

export interface EditNewsType {
  id?: number;
  news: EditNewsItemType[];
}

const ABOUTME_API_ENDPOINT = "Professor/UpdateOverviewOfProfessor";
const ABOUTME_Article_Metric_API_ENDPOINT = "Professor/UpdateOverviewOfProfessorArticleMetric";
const RESEARCH_API_ENDPOINT = "Professor/UpdateResearchInformationOfProfessor";
const EDUCATION_API_ENDPOINT = "Professor/UpdateEducationOfProfessor";
const SOCIAL_LINKS_API_ENDPOINT = "Professor/UpdateSocialNetworksOfProfessor";
const NEWS_API_ENDPOINT = "Professor/UpdateNewsOfProfessor";

export const getProfile = async (): Promise<ProfileType | null> => {
  console.log('getProfile: Starting profile fetch');

  try {
    const { accessToken } = getToken();

    if (!accessToken) {
      console.error('getProfile: No access token found');
      return null;
    }

    const decoded = jwtDecode<{ UserId: string }>(accessToken);
    const userId = decoded.UserId;

    if (!userId) {
      console.error('getProfile: No UserId found in token');
      return null;
    }

    console.log('getProfile: Making API call with userId:', userId);

    // Direct API call without middleware
    const response = await apiProfile.get<ApiResult<ProfileType>>("/Professor/GetProfessorInfo");

    console.log('getProfile: API response received:', response);

    if (!response || !response.data || !response.data.data) {
      console.error('getProfile: Invalid response structure:', response);
      return null;
    }

    // Extract profile data from response
    const profileData = response.data.data;

    // Validate that we have the required fields
    if (!profileData || typeof profileData.id === 'undefined') {
      console.error('getProfile: Invalid profile data:', profileData);
      return null;
    }

    console.log('getProfile: Returning valid profile data:', profileData);
    return profileData;
  } catch (error) {
    console.error('getProfile: Error occurred:', error);
    return null;
  }
};

export const editAboutMeProfile = async (data: EditAboutMeType) => {
  await apiResponseMiddleware<unknown>(
    apiProfile.post(ABOUTME_API_ENDPOINT, data),
    () => { },
    {
      showToast: true,
      loadingMessage: "درحال ذخیره اطلاعات"
    }
  );
};

export const editNewsProfile = async (data: EditNewsType) => {
  await apiResponseMiddleware<unknown>(
    apiProfile.post(NEWS_API_ENDPOINT, data),
    () => { },
    {
      showToast: true,
      loadingMessage: "درحال ذخیره اخبار"
    }
  );
};

export const editAboutMeArticleMetricProfile = async (data: EditAboutMeType) => {
  await apiResponseMiddleware<unknown>(
    apiProfile.post(ABOUTME_Article_Metric_API_ENDPOINT, data),
    () => { },
    {
      showToast: true,
      loadingMessage: "درحال ذخیره اطلاعات"
    }
  );
};


export const editResearchProfile = async (data: EditResearchType) => {
  await apiResponseMiddleware<unknown>(
    apiProfile.post(RESEARCH_API_ENDPOINT, data),
    () => { },
    {
      showToast: true,
      loadingMessage: "درحال ذخیره اطلاعات"
    }
  );
};

export const editEducationProfile = async (data: EditEducationType) => {
  await apiResponseMiddleware<unknown>(
    apiProfile.post(EDUCATION_API_ENDPOINT, data),
    () => { },
    {
      showToast: true,
      loadingMessage: "درحال ذخیره اطلاعات"
    }
  );
};

export const editSocialLinksProfile = async (data: EditSocialLinksType) => {
  await apiResponseMiddleware<unknown>(
    apiProfile.post(SOCIAL_LINKS_API_ENDPOINT, data),
    () => { },
    {
      showToast: true,
      loadingMessage: "درحال ذخیره اطلاعات"
    }
  );
};

export const editProfileImage = async (data: FormData) => {
  await apiResponseMiddleware<unknown>(
    apiProfile.post("/Professor/UpdateImageOfProfessor", data),
    () => { },
    {
      showToast: true,
      loadingMessage: "درحال ارسال عکس"
    }
  );
};

export interface ProfessorListItemType {
  id: number;
  name?: string;
  fullName?: string;
  imageUrl?: string;
  degree?: string;
  position?: string;
  positionTitle?: string;
  email?: string;
  phone?: string;
  userIdentifier?: string;
  child?: Array<{
    id: string;
    link: string;
    title: string;
  }>;
}

export interface ProfessorListResponse {
  data: ProfessorListItemType[];
  message: string;
  status: boolean;
  statusCode: number;
  totalCount: number;
  listSize: number;
  listNumber: number;
}

export const getProfessorList = async (pageSize: number = 10, pageNumber: number = 1): Promise<ProfessorListItemType[]> => {
  try {
    const response = await apiResponseMiddleware<ProfessorListItemType[]>(
      apiProfile.get("/Professor/GetProfileList", {
        params: {
          PageSize: pageSize,
          PageNumber: pageNumber,
          Language: 1
        }
      }),
      () => { },
      {
        showToast: false
      }
    );

    if (!response || !response.data) {
      console.error('getProfessorList: Invalid response structure:', response);
      return [];
    }

    return response.data;
  } catch (error) {
    console.error('getProfessorList: Error occurred:', error);
    return [];
  }
};
